import * as t from 'io-ts';
import _, {assignWith, flatten, isArray, isEmpty, isPlainObject, isUndefined, omit} from 'lodash';

import {isDefined, isNotNull} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {parseConversationTimelineBoundary, RConversationTimelineMetadata} from '../../helpers/viewerHelpers';
import {parseAccess, RAccess} from './accessModel';
import {RAccount} from './accountModel';
import {isBasicActivity, parseActivity, RActivity} from './activityModel';
import {isTeammateActivitySource} from './activitySourceModel';
import {
  AnalyticsTimelineEvent,
  parseAnalyticsTimelineEvent,
  RAnalyticsTimelineEvent,
} from './analyticsTimelineEventModel';
import {RAnswerFolder} from './answerFolderModel';
import {RAnswer} from './answerModel';
import {RApiToken} from './apiTokenModel';
import {makeApiType} from './apiTypes';
import {RApplication} from './applicationModel';
import {parseCalendarEvent, RCalendarEvent} from './calendarEventModel';
import {parseCalendarEventVersion, RCalendarEventVersion} from './calendarEventVersionModel';
import {parseCalendarFromCalendarVersion, RCalendar} from './calendarModel';
import {parseCalendarVersion, RCalendarVersion} from './calendarVersionModel';
import {
  parseCardGroup,
  parseCardGroupsFromConversation,
  parseCardGroupsFromRecipient,
  RCardGroup,
} from './cardGroupModel';
import {parseCard, parseCardsFromConversation, parseCardsFromRecipient, RCard} from './cardModel';
import {RCardNote} from './cardNoteModel';
import {parseChannel, RChannel} from './channelModel';
import {parseComment, parseLastComment, RComment} from './commentModel';
import {
  parseContactFromParticipant,
  parseContactInCard,
  parseContactsFromChannel,
  parseContactsFromConversation,
  parseContactsFromRecipient,
  RContact,
} from './contactModel';
import {parseConversationHighlight, RConversationHighlight} from './conversationHighlightModel';
import {parseConversationMeta, RConversationMeta} from './conversationMetaModel';
import {parseConversation, parseConversationPrecogs, RConversation} from './conversationModel';
import {parseConversationSearchResult, RConversationSearchResult} from './conversationSearchResultModel';
import {RCrmSync} from './crmSyncModel';
import {RCustomField} from './customFieldModel';
import {RExternalChannelType} from './externalChannelTypeModel';
import {parseInbox, RInbox} from './inboxModel';
import {RIntegrationType} from './integrationTypesModel';
import {parseLens, RLens} from './lensModel';
import {RLocalApplication} from './localApplicationModel';
import {
  ConversationSrcMessageKeysEnum,
  MessageModel,
  parseEmlMessage,
  parseMessage,
  parseMessageIncludedInConversation,
  RMessage,
} from './messageModel';
import {RPrecog} from './precogModel';
import {parsePreferences, RPreferences} from './preferencesModel';
import {parseSchedulingLink, RSchedulingLink} from './schedulingLinkModel';
import {RSignature} from './signatureModel';
import {parseTag, RTag} from './tagModel';
import {parseTeammate, RTeammate} from './teammateModel';
import {parseTopicFromActivity, parseTopicsFromConversation, RTopic} from './topicModel';
import {parseVacationResponder, RVacationResponder} from './vacationResponderModel';
import {parseWorkspace, RWorkspace} from './workspaceModel';

/*
 * Runtime type.
 */

const RConversationDuplicates = t.readonly(
  t.type({
    conversationId: t.Integer,
    duplicateIds: t.readonlyArray(t.Integer),
    shouldShowDuplicateBanner: t.boolean,
  }),
);

const RDataGroupBase = t.readonly(
  t.partial({
    limit: t.Integer,
    updatedAt: t.Integer,
    fetchedAt: t.Integer,
    /** Batch IDs corresponding to successful offline actions. */
    acceptedOfflineIds: t.readonlyArray(t.string),
    preferences: RPreferences,
    access: RAccess,
    accounts: t.readonlyArray(RAccount),
    teammates: t.readonlyArray(RTeammate),
    inboxes: t.readonlyArray(RInbox),
    channels: t.readonlyArray(RChannel),
    conversations: t.readonlyArray(RConversation),
    conversationPrecogs: t.readonlyArray(RPrecog),
    conversationSentPrecogs: t.readonlyArray(RPrecog),
    customFields: t.readonlyArray(RCustomField),
    /** When fetching P3, this contains both timeline activities and nested/quoted activities. */
    activities: t.readonlyArray(RActivity),
    analyticsActivities: t.readonlyArray(RAnalyticsTimelineEvent),
    /** When fetching P3, this contains the timeline activity IDs and boundaries provided by the server. */
    timeline: RConversationTimelineMetadata,
    messages: t.readonlyArray(RMessage),
    comments: t.readonlyArray(RComment),
    deletedCommentUids: t.readonlyArray(t.string),
    contacts: t.readonlyArray(RContact),
    cards: t.readonlyArray(RCard),
    cardGroups: t.readonlyArray(RCardGroup),
    cardNotes: t.readonlyArray(RCardNote),
    lenses: t.readonlyArray(RLens),
    tags: t.readonlyArray(RTag),
    calendarEvents: t.readonlyArray(RCalendarEvent),
    calendarEventVersions: t.readonlyArray(RCalendarEventVersion),
    calendars: t.readonlyArray(RCalendar),
    calendarVersions: t.readonlyArray(RCalendarVersion),
    schedulingLinks: t.readonlyArray(RSchedulingLink),
    conversationHighlights: t.readonlyArray(RConversationHighlight),
    conversationMetas: t.readonlyArray(RConversationMeta),
    conversationTotal: t.union([t.undefined, t.Integer]),
    conversationSearchResults: t.readonlyArray(RConversationSearchResult),
    signatures: t.readonlyArray(RSignature),
    answers: t.readonlyArray(RAnswer),
    answerFolders: t.readonlyArray(RAnswerFolder),
    conversationDuplicates: t.readonlyArray(RConversationDuplicates),
    hasMore: t.union([t.undefined, t.boolean]),
    crmSyncs: t.readonlyArray(RCrmSync),
    workspace: RWorkspace,
    vacationResponders: t.readonlyArray(RVacationResponder),
    topics: t.readonlyArray(RTopic),
    externalChannelTypes: t.readonlyArray(RExternalChannelType),
    integrationTypes: t.readonlyArray(RIntegrationType),
    applications: t.readonlyArray(RApplication),
    localApplications: t.readonlyArray(RLocalApplication),
    apiTokens: t.readonlyArray(RApiToken),
  }),
);

// TODO: Figure out why this is necessary.
const RPlainObject = new t.Type<DataGroupModel, DataGroupModel, DataGroupModel>(
  'PlainObject',
  RDataGroupBase.is,
  (dataGroup, context) => (isPlainObject(dataGroup) ? t.success(dataGroup) : t.failure(dataGroup, context)),
  t.identity,
);

/*
 * Type.
 */

export interface DataGroupModel extends t.TypeOf<typeof RDataGroupBase> {}
export const RDataGroup = RDataGroupBase.pipe(RPlainObject, 'RDataGroup');

/*
 * IO methods.
 */

export function parseDataGroupFromPreferences(src: any): DataGroupModel {
  return {
    preferences: parsePreferences(src),
    updatedAt: src.updated_at,
  };
}

export function parseDataGroupFromAccess(src: any): DataGroupModel {
  return {
    access: parseAccess(src),
  };
}

export function parseDataGroupFromTeammate(src: any): DataGroupModel {
  return {
    teammates: [parseTeammate(src)],
    updatedAt: src.updated_at,
  };
}

export function parseDataGroupFromMessage(src: any): DataGroupModel {
  // TEMPORARY: If the message is a draft without a channel ID, don't parse it.
  if (String(src.status).startsWith('draft-') && src.from && !src.from.channel_id) {
    return {};
  }

  return parseMessageBase(parseMessage(src), src);
}

export function parseDataGroupFromMessages(src: any): DataGroupModel {
  const dataGroups: ReadonlyArray<DataGroupModel> = src.messages.map(parseDataGroupFromMessage);

  const messages = flatten(dataGroups.map((dataGroup) => dataGroup.messages).filter(isDefined));
  const cards = flatten(dataGroups.map((dataGroup) => dataGroup.cards).filter(isDefined));

  return {
    messages,
    cards,
  };
}

export function parseDataGroupFromEmlMessage(src: any): DataGroupModel {
  return parseMessageBase(parseEmlMessage(src), src);
}

function parseMessageBase(message: MessageModel, src: any): DataGroupModel {
  // Parse the message.
  const messageData: DataGroupModel = {
    messages: [message],
    contacts: _(src.recipients).map(parseContactsFromRecipient).flatten().value(),
    cards: _(src.recipients).map(parseCardsFromRecipient).flatten().value(),
    cardGroups: _(src.recipients)
      .map(parseCardGroupsFromRecipient)
      .flatten()
      .uniqBy((group) => group.id)
      .value(),
    channels: _(src.recipients)
      .map((r) => r.channel_full)
      .compact()
      .map(parseChannel)
      .value(),
    comments: _(src.annotation_comments || [])
      .map(parseComment)
      .compact()
      .value(),
    updatedAt: src.updated_at,
  };

  // Parse the calendar event versions, if any.
  const calendarEventVersions = src.calendar_event_versions || [];
  const calendarEventVersionsData = calendarEventVersions.map(parseDataGroupFromCalendarEventVersion);

  // Optional properties.
  // eslint-disable-next-line camelcase
  const {conversation, from, variable_interpolation} = src;
  const conversationData = (conversation && parseDataGroupFromConversation(conversation)) || undefined;
  const channelData = (from?.channel && parseDataGroupFromChannel(from.channel)) || undefined;
  // eslint-disable-next-line camelcase
  const variableInterpolationDataGroups = variable_interpolation?.cards?.map(parseDataGroupFromCard) || [];

  return mergeDataGroups(
    messageData,
    // Omit conversation embedded messages so they don't end up in the message list.
    omit(conversationData, 'messages'),
    channelData,
    ...calendarEventVersionsData,
    ...variableInterpolationDataGroups,
  );
}

export function parseDataGroupFromComment(src: any): DataGroupModel {
  // If the comment has been deleted, don't attempt to parse the attached data.
  if (src.deleted_at) {
    return {
      deletedCommentUids: [src.uid],
    };
  }

  const annotationComments = (src.annotation_comments ?? []).map(parseComment);

  const commentData: DataGroupModel = {
    comments: [parseComment(src), ...annotationComments],
    teammates: [parseTeammate(src.author)],
  };

  // Parse the attached conversation if any.
  const conversation = src.conversation || undefined;
  const conversationData = conversation && parseDataGroupFromConversation(conversation);

  // Parse the attached activity if any.
  const activity = src.referenced_activity || undefined;
  const activityData = activity && parseDataGroupFromActivity(activity);

  // Parse the attached conversation/message unfurl data if any.
  const unfurls = src.unfurls || [];
  const unfurlsData = parseDataGroupFromCommentUnfurls(unfurls);

  // Combine and return.
  return mergeDataGroups(commentData, conversationData, activityData, ...unfurlsData);
}

function parseDataGroupFromCommentUnfurls(src: any): DataGroupModel[] {
  if (!Array.isArray(src)) {
    return [];
  }

  return _(src)
    .map((unfurl: any) => {
      if (unfurl.conversation) {
        return parseDataGroupFromConversation(unfurl.conversation);
      }

      if (unfurl.activity) {
        return parseDataGroupFromActivity(unfurl.activity);
      }

      return undefined;
    })
    .compact()
    .value();
}

export function parseDataGroupFromCommentReaction(src: any): DataGroupModel {
  return {
    comments: [parseComment(src.comment)],
    teammates: [parseTeammate(src.comment.author)],
  };
}

export function parseDataGroupFromWorkspace(src: any): DataGroupModel {
  return {workspace: parseWorkspace(src)};
}

export function parseDataGroupFromActivities(src: any): DataGroupModel {
  const activitiesDataGroups = src.activities?.map(parseDataGroupFromActivity);
  return mergeDataGroups(...activitiesDataGroups);
}

export function parseDataGroupFromActivity(src: any): DataGroupModel {
  // Parse the activity.
  const activity = parseActivity(src);
  const activityData: DataGroupModel = {activities: [activity]};

  // Parse the tag, if any.
  const tag = src.tag || undefined;
  const tagData = tag && parseDataGroupFromTag(src.tag);

  // Parse the attached message, if any.
  const message = src.message || undefined;
  const messageData = message && parseDataGroupFromMessage(message);

  // Parse the attached comment, if any.
  const comment = src.comment || undefined;
  const commentData = comment && parseDataGroupFromComment(comment);

  // Parse the attached conversation, if any.
  const conversation = src.conversation || undefined;
  const conversationData = conversation && parseDataGroupFromConversation(conversation);

  // Parse the attached assignee, if any.
  const assignee = src.assignee || undefined;
  const assigneeData = assignee && parseDataGroupFromTeammate(assignee);

  // Parse the attached from teammate for basic teammate activities, if any.
  const isBasicTeammateActivity = isBasicActivity(activity) && isTeammateActivitySource(activity.source);
  const fromTeammate = (isBasicTeammateActivity && src.from) || undefined;
  const fromTeammateData = fromTeammate && parseDataGroupFromTeammate(fromTeammate);

  // Parse the attached inboxes, if any.
  const inboxes = src.inboxes_full || [];
  const inboxesData = inboxes.map(parseDataGroupFromInbox);

  // Parse topic data.
  const topic = parseTopicFromActivity(src);
  const topicsData = {topics: topic ? [topic] : []};

  // Combine and return.
  return mergeDataGroups(
    activityData,
    tagData,
    messageData,
    commentData,
    conversationData,
    assigneeData,
    fromTeammateData,
    topicsData,
    ...inboxesData,
  );
}

/* eslint no-underscore-dangle: 0 */
export function parseDataGroupFromConversation(src: any): DataGroupModel {
  // Parse the conversation.
  const messages = parseMessagesFromConversation(src);
  const conversationData: DataGroupModel = {
    conversations: [parseConversation(src)],
    conversationPrecogs: parseConversationPrecogs(src),
    comments: (src.last_comment && [parseLastComment(src)]) || undefined,
    messages: isEmpty(messages) ? undefined : messages,
    contacts: parseContactsFromConversation(src),
    cards: parseCardsFromConversation(src),
    cardGroups: parseCardGroupsFromConversation(src),
    topics: parseTopicsFromConversation(src),
    conversationHighlights: (src._highlight_ranges && [parseConversationHighlight(src)]) || undefined,
    conversationMetas: [parseConversationMeta(src)],
    teammates: _(src.trackers)
      .map((tea: any) => tea.teammate)
      .compact()
      .map((tea: any) => parseTeammate(tea))
      .value(),
    timeline: parseConversationTimeline(src),
    limit: src.limit,
    acceptedOfflineIds: src.offline_ids || undefined,
    updatedAt: src.updated_at,
  };

  // Parse the tags.
  const tags = src.tags || [];
  const tagsData = tags.map((i: any) => parseDataGroupFromTag(i));

  // Parse the draft messages.
  const draftMessages = src.draft_messages || [];
  const draftMessagesData = draftMessages.map((m: any) => parseDataGroupFromMessage(m));

  // Parse the draft comments.
  const draftComments = src.draft_comments || [];
  const draftCommentsData = draftComments.map((c: any) => parseDataGroupFromComment(c));

  // Parse the timeline, if any.
  const activitiesFromTimeline = src.timeline || [];
  const activitiesDataFromTimeline = activitiesFromTimeline.map(parseDataGroupFromActivity);

  // Parse the created activities, if any
  const activitiesCreated = src.created_activities || [];
  const activitiesCreatedData = _(activitiesCreated)
    .map(parseDataGroupFromActivity)
    .map((dG) => _.omit(dG, 'conversations'))
    .value();

  // Parse the updated activities, if any
  const activitiesUpdated = src.updated_activities || [];
  const activitiesUpdatedData = _(activitiesUpdated)
    .map(parseDataGroupFromActivity)
    .map((dG) => _.omit(dG, 'conversations'))
    .value();

  // Parse the bump activity, if any.
  const bumpActivity = src.bump_activity || undefined;
  const bumpActivityData = bumpActivity && parseDataGroupFromActivity(bumpActivity);

  // Parse the attached inboxes, if any.
  const inboxes = src.inboxes_full || [];
  const inboxesData = inboxes.map(parseDataGroupFromInbox);

  // Parse the attached channels, if any.
  const channels = src.channels_full || [];
  const channelsData = channels.map(parseDataGroupFromChannel);

  // Combine everything and return.
  return mergeDataGroups(
    ...tagsData,
    ...draftMessagesData,
    ...draftCommentsData,
    ...activitiesDataFromTimeline,
    ...activitiesCreatedData,
    ...activitiesUpdatedData,
    bumpActivityData,
    ...inboxesData,
    ...channelsData,
    // Merge the conversation last so its metadata takes precedence, just in case.
    conversationData,
  );
}

export function parseMessagesFromConversation(src: any): MessageModel[] {
  const messages = [];
  const lastMessageUid = src[ConversationSrcMessageKeysEnum.LAST_MESSAGE]?.uid;
  const lastManualMessageUid = src[ConversationSrcMessageKeysEnum.LAST_MANUAL_MESSAGE]?.uid;
  const isSameMessage = lastMessageUid === lastManualMessageUid;

  if (lastMessageUid) {
    messages.push(
      parseMessageIncludedInConversation(src, ConversationSrcMessageKeysEnum.LAST_MESSAGE, {
        isLastMessage: true,
        isLastManualMessage: isSameMessage,
      }),
    );
  }

  if (lastManualMessageUid && !isSameMessage) {
    messages.push(
      parseMessageIncludedInConversation(src, ConversationSrcMessageKeysEnum.LAST_MANUAL_MESSAGE, {
        isLastMessage: false,
        isLastManualMessage: true,
      }),
    );
  }

  return messages;
}

function parseConversationTimeline(src: any) {
  if (!isArray(src.timeline)) {
    return undefined;
  }

  return {
    activityIds: src.timeline.map((activity: any) => activity.id),
    topBoundary: src.before ? parseConversationTimelineBoundary(src.before) : undefined,
    bottomBoundary: src.after ? parseConversationTimelineBoundary(src.after) : undefined,
  };
}

function parseDataGroupFromBatchTags(src: any): DataGroupModel {
  // Parse the tags.
  const tags = src.tags || [];
  const tagsData = tags.map((i: any) => parseDataGroupFromTag(i));

  return mergeDataGroups(...tagsData);
}

function parseDataGroupFromConversationContent(src: any): DataGroupModel {
  const firstActivity = src.timeline[0];

  const conversationDuplicatesItem =
    firstActivity && firstActivity.conversation_id
      ? {
          conversationId: firstActivity.conversation_id,
          duplicateIds: src.duplicate_conversation_ids || [],
          shouldShowDuplicateBanner: Boolean(src.should_show_duplicate_banner),
        }
      : undefined;

  const conversationContentData = {
    timeline: parseConversationTimeline(src),
    limit: src.limit,
    updatedAt: src.updated_at,
    conversationDuplicates: conversationDuplicatesItem ? [conversationDuplicatesItem] : [],
  };

  const activities = src.timeline || null; // NULL to fail during validation if there was no collection.
  const activitiesData = activities && activities.map(parseDataGroupFromActivity);

  const draftMessages = src.draft_messages || [];
  const draftMessagesData = draftMessages.map((m: any) => parseDataGroupFromMessage(m));

  const draftComments = src.draft_comments || [];
  const draftCommentsData = draftComments.map((c: any) => parseDataGroupFromComment(c));

  // conversationContentData is merged last as its own updatedAt should absolutely be the one to be retained.
  return mergeDataGroups(
    ...activitiesData,
    ...draftMessagesData,
    ...draftCommentsData,
    conversationContentData,
  );
}

export function parseDataGroupFromTag(src: any): DataGroupModel {
  // Parse the tag.
  const tagData: DataGroupModel = {
    tags: [parseTag(src)],
  };

  // Parse the parent tag, if any.
  const parentTagData = (src.parent_tag && parseDataGroupFromTag(src.parent_tag)) || undefined;

  // Merge the groups and return.
  return mergeDataGroups(tagData, parentTagData);
}

export function parseDataGroupFromInbox(src: any): DataGroupModel {
  return {
    inboxes: [parseInbox(src)],
  };
}

export function parseDataGroupFromLens(src: any): DataGroupModel {
  return {
    lenses: [parseLens(src)],
  };
}

export function parseDataGroupFromChannel(src: any): DataGroupModel {
  return {
    channels: [parseChannel(src)],
    inboxes: (src.inbox && [parseInbox(src.inbox)]) || undefined,
    contacts: parseContactsFromChannel(src),
  };
}

export function parseDataGroupFromConversationPage(src: any): DataGroupModel {
  const {limit} = src;

  // Parse the conversations.
  const conversationsData = src.conversations.map(parseDataGroupFromConversation);

  // Combine everything and return.
  return mergeDataGroups(...conversationsData, {
    limit,
    fetchedAt: src.fetch_at,
    conversationTotal: src.total,
  });
}

export function parseDataGroupFromConversationSearchResult(src: any): DataGroupModel {
  // Parse the search results.
  const searchResultData: DataGroupModel = {
    conversationSearchResults: [parseConversationSearchResult(src)],
    contacts: parseContactsFromConversation(src),
    cards: parseCardsFromConversation(src),
    cardGroups: parseCardGroupsFromConversation(src),
  };

  return searchResultData;
}

export function parseDataGroupFromConversationSearchResultPage(src: any): DataGroupModel {
  // We should not have conversations but only conversation search results when calling the search_raw endpoint.
  // The backend giving us conversations means we should fallback to the regular search behavior.
  // This, combined with an FF helps de-risk the rollout.
  if ('conversations' in src) {
    return parseDataGroupFromConversationPage(src);
  }

  const {limit} = src;

  // Parse the conversation search results.
  const conversationSearchResultsData = src.conversation_search_results.map(
    parseDataGroupFromConversationSearchResult,
  );

  const inboxes = src.inboxes?.map(parseDataGroupFromInbox) ?? [];
  const tags = src.tags?.map(parseDataGroupFromTag) ?? [];
  const teammates = src.teammates?.map(parseDataGroupFromTeammate) ?? [];

  // Combine everything and return.
  return mergeDataGroups(...conversationSearchResultsData, ...inboxes, ...tags, ...teammates, {
    limit,
    fetchedAt: src.fetch_at,
    conversationTotal: src.total,
  });
}

export function parseDataGroupFromConversationSync(src: any): DataGroupModel {
  // If the server refused to sync, return an empty data group.
  if (src.sync !== 'ok') {
    return {};
  }

  // TODO: Process conversation_deleted and conversation_denied deltas.
  const conversationsData = src.deltas
    .filter((delta: any) => delta.type === 'conversation_updated')
    .map((delta: any) => parseDataGroupFromConversation(delta.conversation));

  return mergeDataGroups(...conversationsData, {fetchedAt: src.sync_at});
}

export function parseDataGroupFromCardConversations(src: any): DataGroupModel {
  // Parse the conversations.
  const conversationsData = src.conversations?.map(parseDataGroupFromConversation) ?? [];

  // Combine everything and return.
  return mergeDataGroups(...conversationsData, {
    hasMore: src.has_more || false,
  });
}

export function parseDataGroupFromDuplicatedConversations(src: any): DataGroupModel {
  // Parse the conversations.
  const conversationsData = src.conversations?.map(parseDataGroupFromConversation) ?? [];

  // Combine everything and return.
  return mergeDataGroups(...conversationsData);
}

export function parseDataGroupFromMessagePage(src: any): DataGroupModel {
  // Parse the messages.
  const messages = src.messages || null; // NULL to fail if there was no collection.
  const messagesData = messages && messages.map((m: any) => parseDataGroupFromMessage(m));

  // TEMPORARY: Subtract from the limit messages that couldn't be parsed.
  const notParsedCount = (messagesData && messagesData.filter((m: any) => !m.messages).length) || 0;

  // Combine everything and return.
  return mergeDataGroups(...messagesData, {
    limit: src.limit - notParsedCount,
    fetchedAt: src.fetch_at,
  });
}

export function parseDataGroupFromActivityPage(src: any): DataGroupModel {
  // Parse the timeline.
  const activities = src.timeline || null; // NULL to fail if there was no collection.
  const activitiesData = activities && activities.map(parseDataGroupFromActivity);

  // Combine everything and return. Merge result last so that its updatedAt wins.
  const result: DataGroupModel = {
    timeline: parseConversationTimeline(src),
    limit: src.limit,
    updatedAt: src.updated_at,
  };
  return mergeDataGroups(...activitiesData, result);
}

// Main entry point for the analytics timeline page parser.
export function parseDataGroupFromAnalyticsActivityPage(src: any): DataGroupModel {
  // Get the list of activities plus all the messages and cards found.
  const {analyticsActivities, messages, cards} = parseDataGroupFromAnalyticsTimelineEvents(src);
  return {
    analyticsActivities,
    messages,
    cards,
    timeline: parseConversationTimeline(src),
    limit: src.limit,
    updatedAt: src.updated_at,
  };
}

// This will parse the list of timeline activities + all the messages and cards found in them.
export function parseDataGroupFromAnalyticsTimelineEvents(src: any): DataGroupModel {
  const analyticsActivities: ReadonlyArray<AnalyticsTimelineEvent> =
    src.timeline?.map(parseAnalyticsTimelineEvent).filter(isNotNull) || [];
  const dataGroups: ReadonlyArray<DataGroupModel> =
    src.timeline
      ?.map((event: any) => (event.message ? parseDataGroupFromMessage(event.message) : null))
      .filter(isNotNull) || [];
  const messages = flatten(dataGroups.map((dataGroup) => dataGroup.messages).filter(isDefined));
  const cards = flatten(dataGroups.map((dataGroup) => dataGroup.cards).filter(isDefined));

  return {
    analyticsActivities,
    messages,
    cards,
  };
}

export function parseDataGroupFromCard(src: any): DataGroupModel {
  return {
    cards: [parseCard(src)],
    contacts: (src.contacts && src.contacts.map((c: any) => parseContactInCard(src, c))) || [],
    cardGroups: src.groups && src.groups.map(parseCardGroup),
  };
}

export function parseDataGroupFromCalendarEventVersion(src: any): DataGroupModel {
  const calendarEventVersionData: DataGroupModel = {
    calendarEventVersions: [parseCalendarEventVersion(src)],
  };

  const calendarEventData = parseDataGroupFromCalendarEvent({
    ...src.calendar_event,
    calendar_event_version_uid: src.uid,
  });

  return mergeTwoDataGroups(calendarEventVersionData, calendarEventData);
}

export function parseDataGroupFromCalendarEvent(src: any): DataGroupModel {
  return {
    calendarEvents: [parseCalendarEvent(src)],
    contacts: src.participants.map(parseContactFromParticipant),
  };
}

export function parseDataGroupFromCalendarEventVersionPage(src: any): DataGroupModel {
  // Parse the calendar event versions.
  const calendarEventVersions = src.calendar_event_versions || null;
  const calendarEventVersionsData =
    calendarEventVersions && calendarEventVersions.map(parseDataGroupFromCalendarEventVersion);

  return mergeDataGroups(...calendarEventVersionsData);
}

export function parseDataGroupFromCalendarVersion(src: any): DataGroupModel {
  return {
    calendarVersions: [parseCalendarVersion(src)],
    calendars: [parseCalendarFromCalendarVersion(src)],
  };
}

export function parseDataGroupFromCalendarVersionPage(src: any): DataGroupModel {
  // Parse the calendar versions.
  const calendarVersions = src.calendar_versions || null;
  const calendarVersionsData = calendarVersions && calendarVersions.map(parseDataGroupFromCalendarVersion);

  return mergeDataGroups(...calendarVersionsData);
}

export function parseDataGroupFromCardPage(src: any): DataGroupModel {
  const dataGroups = (src.cards || []).map(parseDataGroupFromCard);
  return mergeDataGroups(...dataGroups);
}

export function parseDataGroupFromInboxPage(src: any): DataGroupModel {
  const dataGroups = (src.inboxes || []).map(parseDataGroupFromInbox);
  return mergeDataGroups(...dataGroups);
}

function parseDataGroupFromSchedulingLinkPage(src: any): DataGroupModel {
  return {
    limit: src.limit,
    schedulingLinks: src.scheduling_links && src.scheduling_links.map(parseSchedulingLink),
  };
}

function parseDataGroupFromVacationResponder(src: any): DataGroupModel {
  const vacationResponder = parseVacationResponder(src);

  return {
    vacationResponders: [vacationResponder],
  };
}

export function mergeDataGroups(...groups: Array<DataGroupModel | undefined>): DataGroupModel {
  return groups.reduce<DataGroupModel>(
    (result, item) => (item ? mergeTwoDataGroups(result, item) : result),
    {},
  );
}

function mergeTwoDataGroups(group1: DataGroupModel, group2: DataGroupModel) {
  return assignWith({}, group1, group2, (prop1, prop2) => {
    if (isUndefined(prop2)) {
      return prop1;
    }

    if (isUndefined(prop1)) {
      return prop2;
    }

    if (!Array.isArray(prop1) || !Array.isArray(prop2)) {
      return undefined;
    }

    return prop1.concat(prop2);
  });
}

export function collapseById<T extends {id: number}>(items: ReadonlyArray<T>) {
  return _(items)
    .keyBy((i) => i.id)
    .values()
    .value();
}

export function collapseByUid<T extends {uid: string}>(items: ReadonlyArray<T>) {
  return _(items)
    .keyBy((i) => i.uid)
    .values()
    .value();
}

/*
 * API types.
 */

export const ApiPreferencesDataGroup = makeApiType(
  'preferencesDataGroup',
  RDataGroup,
  parseDataGroupFromPreferences,
);

export const ApiAccessDataGroup = makeApiType('accessDataGroup', RDataGroup, parseDataGroupFromAccess);

export const ApiTeammateDataGroup = makeApiType('teammateDataGroup', RDataGroup, parseDataGroupFromTeammate);

export const ApiConversationDataGroup = makeApiType(
  'conversationDataGroup',
  RDataGroup,
  parseDataGroupFromConversation,
);

export const ApiConversationContentDataGroup = makeApiType(
  'conversationContentDataGroup',
  RDataGroup,
  parseDataGroupFromConversationContent,
);

export const ApiWorkspaceDataGroup = makeApiType(
  'workspaceDataGroup',
  RDataGroup,
  parseDataGroupFromWorkspace,
);

export const ApiMessageDataGroup = makeApiType('messageDataGroup', RDataGroup, parseDataGroupFromMessage);
export const ApiMessagesDataGroup = makeApiType('messagesDataGroup', RDataGroup, parseDataGroupFromMessages);
export const ApiEmlMessageDataGroup = makeApiType(
  'emlMessageDataGroup',
  RDataGroup,
  parseDataGroupFromEmlMessage,
);

export const ApiCommentDataGroup = makeApiType('commentDataGroup', RDataGroup, parseDataGroupFromComment);

export const ApiCommentFromReactionDataGroup = makeApiType(
  'commentFromReactionDataGroup',
  RDataGroup,
  parseDataGroupFromCommentReaction,
);

export const ApiActivitiesDataGroup = makeApiType(
  'activitiesDataGroup',
  RDataGroup,
  parseDataGroupFromActivities,
);
export const ApiActivityDataGroup = makeApiType('activityDataGroup', RDataGroup, parseDataGroupFromActivity);

export const ApiConversationPageDataGroup = makeApiType(
  'conversationPageDataGroup',
  RDataGroup,
  parseDataGroupFromConversationPage,
);

export const ApiConversationSearchResultPageDataGroup = makeApiType(
  'conversationSearchResultPageDataGroup',
  RDataGroup,
  parseDataGroupFromConversationSearchResultPage,
);

export const ApiConversationSyncDataGroup = makeApiType(
  'conversationSyncDataGroup',
  RDataGroup,
  parseDataGroupFromConversationSync,
);

export const ApiCardConversationsDataGroup = makeApiType(
  'cardConversationsDataGroup',
  RDataGroup,
  parseDataGroupFromCardConversations,
);

export const ApiDuplicatedConversationsDataGroup = makeApiType(
  'duplicatedConversationsDataGroup',
  RDataGroup,
  parseDataGroupFromDuplicatedConversations,
);

export const ApiMessagePageDataGroup = makeApiType(
  'messagePageDataGroup',
  RDataGroup,
  parseDataGroupFromMessagePage,
);

export const ApiActivityPageDataGroup = makeApiType(
  'activityPageDataGroup',
  RDataGroup,
  parseDataGroupFromActivityPage,
);

export const ApiAnalyticsActivityPageDataGroup = makeApiType(
  'analyticsActivityPageDataGroup',
  RDataGroup,
  parseDataGroupFromAnalyticsActivityPage,
);

export const ApiCardDataGroup = makeApiType('cardDataGroup', RDataGroup, parseDataGroupFromCard);

export const ApiCalendarEventDataGroup = makeApiType(
  'calendarEventDataGroup',
  RDataGroup,
  parseDataGroupFromCalendarEvent,
);

export const ApiCalendarEventVersionDataGroup = makeApiType(
  'calendarEventVersionDataGroup',
  RDataGroup,
  parseDataGroupFromCalendarEventVersion,
);

export const ApiCalendarEventVersionPageDataGroup = makeApiType(
  'calendarEventVersionPageDataGroup',
  RDataGroup,
  parseDataGroupFromCalendarEventVersionPage,
);

export const ApiCalendarVersionDataGroup = makeApiType(
  'calendarVersionDataGroup',
  RDataGroup,
  parseDataGroupFromCalendarVersion,
);

export const ApiCalendarVersionPageDataGroup = makeApiType(
  'calendarVersionPageDataGroup',
  RDataGroup,
  parseDataGroupFromCalendarVersionPage,
);

export const ApiCardPageDataGroup = makeApiType('cardPageDataGroup', RDataGroup, parseDataGroupFromCardPage);

export const ApiInboxDataGroup = makeApiType('inboxDataGroup', RDataGroup, parseDataGroupFromInbox);

export const ApiInboxPageDataGroup = makeApiType(
  'inboxPageDataGroup',
  RDataGroup,
  parseDataGroupFromInboxPage,
);

export const ApiChannelDataGroup = makeApiType('channel', RDataGroup, parseDataGroupFromChannel);

export const ApiSchedulingLinkDataGroup = makeApiType(
  'schedulingLinkPageDataGroup',
  RDataGroup,
  parseDataGroupFromSchedulingLinkPage,
);

export const ApiVacationResponderDataGroup = makeApiType(
  'vacationResponder',
  RDataGroup,
  parseDataGroupFromVacationResponder,
);

export const ApiBatchTagsDataGroup = makeApiType('batchTags', RDataGroup, parseDataGroupFromBatchTags);
